import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ImageIcon, Download, Search, Loader2, CheckCircle2, AlertTriangle, Info } from 'lucide-react';
import { API } from '@/config';

/**
 * ImageSync — Admin tool to sync missing images from production (drivemaster.se) to preview.
 * Protects against false "broken image" detections when preview and production have separate disks.
 */
const ImageSync = ({ token, i18n }) => {
  const ar = i18n.language === 'ar';
  const [loading, setLoading] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const runAudit = async () => {
    setAuditLoading(true);
    setAudit(null);
    try {
      const res = await axios.get(`${API}/admin/images/audit-safe`, { headers });
      setAudit(res.data);
      const { truly_broken_count, preview_only_broken_count } = res.data;
      if (preview_only_broken_count > 0) {
        toast.success(ar
          ? `تم الفحص: ${preview_only_broken_count} صورة مفقودة محلياً لكنها موجودة على Production — يمكن مزامنتها.`
          : `Audit done: ${preview_only_broken_count} images missing locally but exist on production — can sync.`);
      } else if (truly_broken_count > 0) {
        toast.warning(ar
          ? `${truly_broken_count} صورة مكسورة على كل الخوادم.`
          : `${truly_broken_count} images broken everywhere.`);
      } else {
        toast.success(ar ? 'كل الصور سليمة!' : 'All images healthy!');
      }
    } catch (e) {
      toast.error(ar ? 'فشل الفحص' : 'Audit failed');
      console.error(e);
    } finally {
      setAuditLoading(false);
    }
  };

  const runSync = async () => {
    setLoading(true);
    setSyncResult(null);
    try {
      const res = await axios.post(`${API}/admin/images/sync-from-production`, {}, { headers, timeout: 180000 });
      setSyncResult(res.data);
      const { downloaded_count, failed_count, already_present } = res.data;
      toast.success(ar
        ? `تم التحميل: ${downloaded_count} صورة، فشل: ${failed_count}، موجودة مسبقاً: ${already_present}`
        : `Downloaded: ${downloaded_count}, Failed: ${failed_count}, Already present: ${already_present}`);
      // Refresh audit
      if (audit) runAudit();
    } catch (e) {
      toast.error(ar ? 'فشلت المزامنة' : 'Sync failed');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="image-sync-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-cyan-500 via-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <ImageIcon className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              {ar ? 'مزامنة الصور من Production' : 'Sync Images from Production'}
            </h2>
            <p className="text-white/90 text-sm mt-1 max-w-2xl">
              {ar
                ? 'يحمّل تلقائياً كل الصور المفقودة محلياً من drivemaster.se. آمن — لا يحذف شيئاً، فقط ينسخ ما هو ناقص.'
                : 'Automatically downloads images that are missing locally from drivemaster.se. Safe — it never deletes; only copies what is missing.'}
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <strong className="block mb-1">
            {ar ? 'لماذا تحتاج هذه الأداة؟' : 'Why this tool?'}
          </strong>
          <p className="text-amber-700 dark:text-amber-300 leading-relaxed">
            {ar
              ? 'Preview و Production يتشاركان قاعدة بيانات واحدة لكن قرصين منفصلين. عند رفع صورة على الموقع المنشور، تظهر للمستخدمين فوراً لكن Preview يعتبرها مفقودة حتى تستخدم زر المزامنة.'
              : 'Preview and Production share one database but have separate disks. Images uploaded on production appear immediately for users but show as missing on preview until you run a sync.'}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Audit */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Search className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {ar ? '1. فحص الصور' : '1. Audit Images'}
            </h3>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {ar
              ? 'يفحص كل الأسئلة ويقارن الصور المفقودة محلياً مع Production.'
              : 'Scans all questions and compares locally missing images with production.'}
          </p>
          <Button
            onClick={runAudit}
            disabled={auditLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="image-audit-btn"
          >
            {auditLoading ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Search className="w-4 h-4 me-2" />}
            {ar ? 'ابدأ الفحص' : 'Run Audit'}
          </Button>
        </div>

        {/* Sync */}
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Download className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {ar ? '2. تشغيل المزامنة' : '2. Run Sync'}
            </h3>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            {ar
              ? 'يحمّل كل الصور المفقودة من drivemaster.se إلى القرص المحلي.'
              : 'Downloads all missing images from drivemaster.se to the local disk.'}
          </p>
          <Button
            onClick={runSync}
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="image-sync-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : <Download className="w-4 h-4 me-2" />}
            {ar ? 'ابدأ المزامنة الآن' : 'Start Sync Now'}
          </Button>
        </div>
      </div>

      {/* Audit Results */}
      {audit && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm" data-testid="audit-results">
          <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">
            {ar ? 'نتائج الفحص' : 'Audit Results'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border-2 border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/20 p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-900 dark:text-emerald-300">
                  {ar ? 'قابل للمزامنة (موجود على Production)' : 'Syncable (exists on production)'}
                </span>
              </div>
              <p className="text-3xl font-black text-emerald-700 dark:text-emerald-400">{audit.preview_only_broken_count}</p>
            </div>

            <div className="rounded-lg border-2 border-rose-200 dark:border-rose-800/50 bg-rose-50 dark:bg-rose-950/20 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                <span className="text-sm font-semibold text-rose-900 dark:text-rose-300">
                  {ar ? 'مكسور فعلاً (على كل الخوادم)' : 'Truly broken (everywhere)'}
                </span>
              </div>
              <p className="text-3xl font-black text-rose-700 dark:text-rose-400">{audit.truly_broken_count}</p>
            </div>
          </div>

          {audit.preview_only_broken?.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-blue-600">
                {ar ? 'عرض الأسئلة القابلة للمزامنة' : 'Show syncable questions'} ({audit.preview_only_broken.length})
              </summary>
              <div className="mt-2 max-h-80 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                {audit.preview_only_broken.map((q, i) => (
                  <div key={q.id} className={`p-2 text-xs ${i % 2 === 0 ? 'bg-slate-50 dark:bg-slate-900/50' : ''}`}>
                    <code className="text-slate-400">{q.id.slice(0, 8)}</code>
                    <span className="ms-2 text-slate-700 dark:text-slate-300">{q.sv}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Sync Results */}
      {syncResult && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm" data-testid="sync-results">
          <h3 className="text-lg font-semibold mb-4 text-slate-900 dark:text-slate-100">
            {ar ? 'نتيجة المزامنة' : 'Sync Result'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/50 p-4 text-center">
              <p className="text-xs text-emerald-700 dark:text-emerald-400 font-semibold uppercase tracking-wide mb-1">
                {ar ? 'تم التحميل' : 'Downloaded'}
              </p>
              <p className="text-2xl font-black text-emerald-700 dark:text-emerald-400">{syncResult.downloaded_count}</p>
            </div>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 p-4 text-center">
              <p className="text-xs text-slate-600 dark:text-slate-400 font-semibold uppercase tracking-wide mb-1">
                {ar ? 'موجودة مسبقاً' : 'Already present'}
              </p>
              <p className="text-2xl font-black text-slate-700 dark:text-slate-300">{syncResult.already_present}</p>
            </div>
            <div className="rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/50 p-4 text-center">
              <p className="text-xs text-rose-700 dark:text-rose-400 font-semibold uppercase tracking-wide mb-1">
                {ar ? 'فشل' : 'Failed'}
              </p>
              <p className="text-2xl font-black text-rose-700 dark:text-rose-400">{syncResult.failed_count}</p>
            </div>
          </div>

          {syncResult.failed?.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm font-medium text-rose-700 dark:text-rose-400 hover:underline">
                {ar ? 'عرض الفشل' : 'Show failures'} ({syncResult.failed.length})
              </summary>
              <div className="mt-2 max-h-60 overflow-y-auto border border-rose-200 dark:border-rose-800 rounded-lg">
                {syncResult.failed.map((f, i) => (
                  <div key={i} className="p-2 text-xs border-b border-rose-100 dark:border-rose-900/50">
                    <code className="text-rose-600">{f.collection}/{f.id?.slice(0, 8)}</code>
                    <span className="ms-2 text-slate-500">status={f.status || f.error}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageSync;
